import React, { useState } from "react";
import ReactModal from "react-modal";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { useTicketPaginationQuery } from "../../Services/ticket/TicketList";
import { useMarkTicketActionableMutation } from "../../Services/ticket/CreateTicket";
import { INCOMING_CALL } from "../../constants";
import LoadingSpinner from "../../components/LoadingSpinner";
import { capitalizeFirstLetter } from "../../components/reusableComponent/CapitalLetter";
import ConfirmationModal from "../../components/reusableComponent/NonActionableConfirmationModal";

const PAGE_SIZE = 10;

const TABLE_HEADERS = [
  "Ref ID",
  "Title",
  "Status",
  "Priority",
  "Assignee",
  "Customer",
  "Workflow",
  "Created At",
  "Action",
];

const MODAL_STYLES: ReactModal.Styles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    maxWidth: "1300px",
    maxHeight: "80vh",
    overflowY: "auto",
    padding: "0",
    border: "none",
    borderRadius: "12px",
    position: "relative",
  },
  overlay: {
    backgroundColor: "rgba(0,0,0,0.45)",
    zIndex: 1050,
  },
};

export interface NonActionableModalProps {
  isOpen: boolean;
  onClose: () => void;
  queryParams: {
    value: string;
    selectedWorkFlow: string;
    selectedTeamId: string | null;
    selectedFilterAndValue1: Record<string, unknown>;
    liftingKey: unknown[];
    storeKey: string;
    channel: string;
  };
  onTicketToggled: () => void;
}

const getPriorityColor = (priority?: string): string => {
  switch (priority?.toLowerCase()) {
    case "high":
      return "#ef4444";
    case "medium":
      return "#f97316";
    case "low":
      return "#22c55e";
    default:
      return "#6b7280";
  }
};

const getStatusBadgeStyle = (statusType: string): React.CSSProperties => {
  switch (statusType) {
    case "active":
      return {
        backgroundColor: "#dcfce7",
        color: "#166534",
        border: "1px solid #bbf7d0",
      };
    case "closed":
      return {
        backgroundColor: "#e0e7ff",
        color: "#4338ca",
        border: "1px solid #c7d2fe",
      };
    case "reopen":
      return {
        backgroundColor: "#fee2e2",
        color: "#991b1b",
        border: "1px solid #fecaca",
      };
    case "wip":
      return {
        backgroundColor: "#fef9c3",
        color: "#854d0e",
        border: "1px solid #fef08a",
      };
    default:
      return {
        backgroundColor: "#f3f4f6",
        color: "#374151",
        border: "1px solid #e5e7eb",
      };
  }
};

function buildPageNumbers(
  totalPages: number,
  current: number,
): (number | "...")[] {
  return Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter((p) => p === 1 || p === totalPages || Math.abs(p - current) <= 1)
    .reduce<(number | "...")[]>((acc, p, i, arr) => {
      if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push("...");
      acc.push(p);
      return acc;
    }, []);
}

const NonActionableModal: React.FC<NonActionableModalProps> = ({
  isOpen,
  onClose,
  queryParams,
  onTicketToggled,
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState<{
    ticketId: number;
  } | null>(null);

  const {
    data,
    isLoading,
    refetch: refetchList,
  } = useTicketPaginationQuery(
    {
      currentPage: page,
      pageSize: PAGE_SIZE,
      value: queryParams.value,
      selectedWorkFlow: queryParams.selectedWorkFlow,
      selectedTeamId: queryParams.selectedTeamId,
      selectedFilterAndValue1: {
        ...queryParams.selectedFilterAndValue1,
        is_non_actionable: true,
      },
      liftingKey: queryParams.liftingKey,
      storeKey: queryParams.storeKey,
      channel: queryParams.channel,
    },
    { skip: !queryParams.selectedWorkFlow || !isOpen },
  );

  const tickets = data?.results ?? [];
  const totalCount: number = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const [triggerMarkActionable] = useMarkTicketActionableMutation();
  const [pending, setPending] = useState(false);

  const handleMarkActionable = async (ticketId: number) => {
    setPending(true);
    try {
      const response = await triggerMarkActionable(ticketId);

      if (response?.data) {
        const isCallActive = localStorage.getItem(INCOMING_CALL) === "true";
        void isCallActive;
        onTicketToggled();
        refetchList();

        const newTotalPages = Math.max(
          1,
          Math.ceil((totalCount - 1) / PAGE_SIZE),
        );
        if (page > newTotalPages) setPage(newTotalPages);

        toast.success("Ticket marked as actionable");
      } else if (response?.error) {
        toast.error("Unable to update non-actionable status");
      }
    } catch {
      toast.error("An error occurred while updating non-actionable status");
    } finally {
      setPending(false);
    }
  };

  const handleConfirm = () => {
    if (confirmPayload) {
      handleMarkActionable(confirmPayload.ticketId);
    }
    setConfirmPayload(null);
  };

  const handleOpenTicket = (ticketId: number) => {
    onClose();
    navigate(`/ticket/information/${ticketId}`);
  };

  return (
    <>
      <ReactModal
        isOpen={isOpen}
        onRequestClose={onClose}
        onAfterOpen={() => setPage(1)}
        style={MODAL_STYLES}
        contentLabel="Non-Actionable Tickets"
        shouldCloseOnOverlayClick
      >
        {pending && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 100,
              backgroundColor: "rgba(255, 255, 255, 0.70)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "12px",
              cursor: "wait",
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <LoadingSpinner />
          </div>
        )}

        {/* ── Header ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px 16px",
            borderBottom: "1px solid #e5e7eb",
            position: "sticky",
            top: 0,
            backgroundColor: "#fff",
            zIndex: 1,
            borderRadius: "12px 12px 0 0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "8px",
                backgroundColor: "#fef3c7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="bi-ticket-perforated-fill"
                style={{ color: "#d97706", fontSize: "16px" }}
              />
            </div>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "17px",
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                Non-Actionable Tickets
              </h2>
              <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
                {totalCount} ticket{totalCount !== 1 ? "s" : ""} marked as
                non-actionable
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "6px",
              borderRadius: "6px",
              color: "#6b7280",
              fontSize: "18px",
              lineHeight: 1,
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = "#f3f4f6")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: "16px 24px 0" }}>
          {isLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <LoadingSpinner />
            </div>
          ) : tickets.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "220px",
                gap: "12px",
                color: "#9ca3af",
              }}
            >
              <i className="bi bi-inbox" style={{ fontSize: "40px" }} />
              <p style={{ margin: 0, fontSize: "15px", fontWeight: 500 }}>
                No non-actionable tickets found
              </p>
              <p style={{ margin: 0, fontSize: "13px" }}>
                All tickets are currently actionable
              </p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "13px",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f9fafb" }}>
                    {TABLE_HEADERS.map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: "10px 12px",
                          textAlign: "left",
                          fontWeight: 600,
                          color: "#374151",
                          borderBottom: "1px solid #e5e7eb",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {tickets.map((ticket, idx) => (
                    <tr
                      key={ticket.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenTicket(ticket.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleOpenTicket(ticket.id);
                        }
                      }}
                      style={{
                        backgroundColor: idx % 2 === 0 ? "#fff" : "#fafafa",
                        transition: "background 0.12s",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f0f9ff")
                      }
                      onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        idx % 2 === 0 ? "#fff" : "#fafafa")
                      }
                    >
                      {/* Ref ID */}
                      <td
                        style={{
                          padding: "10px 12px",
                          borderBottom: "1px solid #f3f4f6",
                          color: "#6366f1",
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      >
                        {ticket.id}
                      </td>

                      {/* Title */}
                      <td
                        style={{
                          padding: "10px 12px",
                          borderBottom: "1px solid #f3f4f6",
                          maxWidth: 180,
                        }}
                      >
                        <Tooltip
                          title={ticket.title || ""}
                          arrow
                          placement="top"
                        >
                          <span
                            style={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              color: "#111827",
                              fontWeight: 500,
                            }}
                          >
                            {ticket.title || "—"}
                          </span>
                        </Tooltip>
                      </td>

                      {/* Status */}
                      <td
                        style={{
                          padding: "10px 12px",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <span
                          style={{
                            ...getStatusBadgeStyle(ticket.status_type),
                            padding: "2px 8px",
                            borderRadius: "999px",
                            fontSize: "11px",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ticket.status_display?.toUpperCase() || "—"}
                        </span>
                      </td>

                      {/* Priority */}
                      <td
                        style={{
                          padding: "10px 12px",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                          }}
                        >
                          <i
                            className="bi bi-flag-fill"
                            style={{
                              color: getPriorityColor(
                                ticket.action?.disposition?.priority,
                              ),
                              fontSize: "12px",
                            }}
                          />
                          <span
                            style={{
                              color: "#374151",
                              textTransform: "capitalize",
                            }}
                          >
                            {ticket.action?.disposition?.priority || "—"}
                          </span>
                        </div>
                      </td>

                      {/* Assignee */}
                      <td
                        style={{
                          padding: "10px 12px",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <div
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: "50%",
                              backgroundColor: "#e0e7ff",
                              color: "#6366f1",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "10px",
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {ticket.action?.assignee?.username
                              ?.charAt(0)
                              ?.toUpperCase() || "U"}
                          </div>
                          <span
                            style={{ color: "#374151", whiteSpace: "nowrap" }}
                          >
                            {capitalizeFirstLetter(
                              ticket.action?.assignee?.username?.split(
                                "@",
                              )[0] || "Unassigned",
                            )}
                          </span>
                        </div>
                      </td>

                      {/* Customer */}
                      <td
                        style={{
                          padding: "10px 12px",
                          borderBottom: "1px solid #f3f4f6",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {capitalizeFirstLetter(
                          ticket.action?.contact?.name || "CRM Portal",
                        )}
                      </td>

                      {/* Workflow */}
                      <td
                        style={{
                          padding: "10px 12px",
                          borderBottom: "1px solid #f3f4f6",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {ticket.action?.disposition?.workflow?.name || "—"}
                      </td>

                      {/* Created At */}
                      <td
                        style={{
                          padding: "10px 12px",
                          borderBottom: "1px solid #f3f4f6",
                          whiteSpace: "nowrap",
                          color: "#6b7280",
                        }}
                      >
                        {ticket.created
                          ? format(new Date(ticket.created), "dd-MM-yyyy HH:mm")
                          : "—"}
                      </td>

                      <td
                        style={{
                          padding: "10px 12px",
                          borderBottom: "1px solid #f3f4f6",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip
                          title="Mark as actionable"
                          arrow
                          placement="top"
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              cursor: pending ? "wait" : "pointer",
                            }}
                          >
                            <button
                              disabled={pending}
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmPayload({ ticketId: ticket.id });
                                setConfirmModalOpen(true);
                              }}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                                padding: "4px 10px",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                background: "transparent",
                                cursor: pending ? "wait" : "pointer",
                                fontSize: "12px",
                                color: "#6b7280",
                                whiteSpace: "nowrap",
                                opacity: pending ? 0.7 : 1,
                              }}
                              onMouseEnter={(e) => {
                                if (pending) return;
                                e.currentTarget.style.backgroundColor = "#fef9c3";
                                e.currentTarget.style.borderColor = "#d97706";
                                e.currentTarget.style.color = "#92400e";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                                e.currentTarget.style.borderColor = "#d1d5db";
                                e.currentTarget.style.color = "#6b7280";
                              }}
                            >
                              <i
                                className="bi bi-envelope-check"
                                style={{ fontSize: "13px" }}
                              />
                              Mark Actionable
                            </button>
                          </span>
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Footer / Pagination ── */}
        {!isLoading && tickets.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 24px",
              borderTop: "1px solid #e5e7eb",
              marginTop: "12px",
              backgroundColor: "#f9fafb",
              borderRadius: "0 0 12px 12px",
            }}
          >
            <span style={{ fontSize: "13px", color: "#6b7280" }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
            </span>

            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                style={{
                  padding: "5px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  background: page === 1 ? "#f3f4f6" : "#fff",
                  cursor: page === 1 ? "not-allowed" : "pointer",
                  color: page === 1 ? "#9ca3af" : "#374151",
                  fontSize: "13px",
                }}
              >
                ← Prev
              </button>

              {buildPageNumbers(totalPages, page).map((p, i) =>
                p === "..." ? (
                  <span
                    key={`ellipsis-${i}`}
                    style={{
                      padding: "0 4px",
                      color: "#9ca3af",
                      fontSize: "13px",
                    }}
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p as number)}
                    style={{
                      padding: "5px 10px",
                      border: "1px solid",
                      borderRadius: "6px",
                      borderColor: page === p ? "#6366f1" : "#d1d5db",
                      background: page === p ? "#6366f1" : "#fff",
                      color: page === p ? "#fff" : "#374151",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: page === p ? 600 : 400,
                      minWidth: 32,
                    }}
                  >
                    {p}
                  </button>
                ),
              )}

              <button
                disabled={page === totalPages}
                onClick={() => setPage((p) => p + 1)}
                style={{
                  padding: "5px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  background: page === totalPages ? "#f3f4f6" : "#fff",
                  cursor: page === totalPages ? "not-allowed" : "pointer",
                  color: page === totalPages ? "#9ca3af" : "#374151",
                  fontSize: "13px",
                }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </ReactModal>

      <ConfirmationModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setConfirmPayload(null);
        }}
        onConfirm={handleConfirm}
        title="Confirm Action"
        message={
          <>
            Are you sure you want to{" "}
            <strong>mark this ticket as actionable</strong>?
          </>
        }
        confirmLabel="Confirm"
        confirmButtonClass="btn btn-primary"
      />
    </>
  );
};

export default NonActionableModal;